import {
  USER_ROLES,
  FIELD_TYPES,
  PROGRAMMING_LANGUAGES,
  COURSE_FOR,
  DURATION,
  THUMBNAIL_CONTENT_TYPE,
  REQUEST_TYPES,
  PAYMENT_STATUS,
  REQUEST_STATUS,
  SUBMISSION_STATUSES,
  STATUS,
  CONTENT_TYPE,
  SOCIAL_PLATFORMS,
  VISIBILITY,
} from "./constants";
import {
  ADMIN_USER_BY_ID,
  ADMIN_USERS,
  PREMIUM_PLAN_PURCHASE_DETAILS,
  PREMIUM_PLANS,
  ADMIN_REPORTS,
  GET_USER_PROBLEM_SUBMISSION_DETAILS,
  ADMIN_USER_PLANS,
  TAGS,
  ADMIN_USER_STREAKS,
  ADMIN_PROBLEM_LISTS,
} from "./paths";
import { ROUTES } from "./routeConstants";
import EditNoteIcon from "@mui/icons-material/EditNote";
import SettingsIcon from "@mui/icons-material/Settings";
import ExtensionIcon from "@mui/icons-material/Extension";
import ProblemListPage from "app/pages/Problems/ProblemList";
import ProblemTabs from "app/pages/ProblemForm/ProblemTabs";
import ProblemDetailPage from "app/pages/ProblemDetail/ProblemDetail";
import AdminProgrammingContentList from "app/pages/ProgrammingContent/ProgrammingContentList";
import AdminProgrammingContentForm from "app/pages/ProgrammingContent/AdminProgrammingContentForm";
import ProgrammingContentPage from "app/pages/ProgrammingContent/ProgrammingContentDetail";
import PricingPlan from "app/pages/Pricing/PricingList/PricingPlan";
import PricingDetails from "app/pages/Pricing/PricingDetails";
import BugReportIcon from "@mui/icons-material/BugReport";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import PersonAddAltIcon from "@mui/icons-material/PersonAddAlt";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AddCardIcon from "@mui/icons-material/AddCard";
import TagIcon from "@mui/icons-material/Tag";
import StoreIcon from "@mui/icons-material/Store";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import ListIcon from "@mui/icons-material/List";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";

export const UserEntity = {
  sectionTitle: "User Management",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_USERS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_USERS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_USERS_ADD,
      component: null,
    },
  },
  backendApiPaths: {
    create: ADMIN_USERS,
    getById: ADMIN_USER_BY_ID,
    getList: ADMIN_USERS,
    update: ADMIN_USERS,
    delete: `${ADMIN_USERS}/:id`,
  },
  dashboardCards: [
    {
      title: "Create a New User",
      description: "Admin-only: Add a new user to the platform",
      icon: (
        <PersonAddAltIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="primary"
        />
      ),
      route: ROUTES.ADMIN_USERS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Users",
      description: "Admin-only: View, update, or delete users",
      icon: (
        <ManageAccountsIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="success"
        />
      ),
      route: ROUTES.ADMIN_USERS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        minWidth: 80,
        isHiddenInForm: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      login: {
        type: FIELD_TYPES.STRING,
        label: "Login",
        listTableSticky: true,
        listTableStickyLeft: 80,
        minWidth: 150,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          regex: /^[a-zA-Z0-9_-]{3,50}$/,
          message: "Login must be 3-50 characters (letters, numbers, _, -)",
        },
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      firstName: {
        type: FIELD_TYPES.STRING,
        label: "First Name",
        minWidth: 100,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          regex: /^[a-zA-Z\s]{3,50}$/,
          message: "First Name must be 3-50 letters or spaces",
        },
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      lastName: {
        type: FIELD_TYPES.STRING,
        label: "Last Name",
        minWidth: 100,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          regex: /^[a-zA-Z\s]{1,50}$/,
          message: "Last Name must be 1-50 letters or spaces",
        },
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      email: {
        type: FIELD_TYPES.STRING,
        label: "Email",
        minWidth: 100,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          regex: /^[\w-]+@([\w-]+\.)+[\w-]{2,4}$/,
          message: "Enter a valid email (e.g., abc@domain.com)",
        },
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      imageUrl: {
        type: FIELD_TYPES.STRING,
        label: "Image URL",
        minWidth: 100,
        isHiddenInTable: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      authorities: {
        type: FIELD_TYPES.ARRAY,
        label: "Roles",
        minWidth: 100,
        required: true,
        options: Object.keys(USER_ROLES).filter((key) => key !== "colorMap"),
        colorMap: USER_ROLES.colorMap,
        labelMap: {
          ROLE_ADMIN: "Admin",
          ROLE_USER: "User",
          ROLE_PREMIUM_USER: "Premium User",
          ROLE_WRITER: "Writer",
          ROLE_STUDENT: "Student",
          ROLE_TEACHER: "Teacher",
        },
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      langKey: {
        type: FIELD_TYPES.STRING,
        label: "Language",
        minWidth: 100,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          regex: /^[a-z]{2}$/,
          message: "Language must be a 2-letter code (e.g., en)",
        },
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      activated: {
        type: FIELD_TYPES.BOOLEAN,
        label: "Activated",
        minWidth: 100,
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      createdBy: {
        type: FIELD_TYPES.STRING,
        label: "Created By",
        minWidth: 100,
        isHiddenInForm: true,
        isHiddenInTable: true,
        isIncludedInPayload: false,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      createdDate: {
        type: FIELD_TYPES.DATE,
        label: "Created Date",
        minWidth: 100,
        isHiddenInForm: true,
        isIncludedInPayload: false,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      lastModifiedBy: {
        type: FIELD_TYPES.STRING,
        label: "Last Modified By",
        minWidth: 100,
        isHiddenInForm: true,
        isIncludedInPayload: false,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      lastModifiedDate: {
        type: FIELD_TYPES.DATE,
        label: "Last Modified Date",
        minWidth: 100,
        isHiddenInForm: true,
        isIncludedInPayload: false,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const ProblemEntity = {
  sectionTitle: "DSA Problems",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_PROBLEMS,
      component: ProblemListPage,
    },
    add: {
      path: ROUTES.ADMIN_PROBLEMS_ADD,
      component: ProblemTabs,
    },
    update: {
      path: ROUTES.ADMIN_PROBLEMS_UPDATE,
      component: ProblemTabs,
    },
    detail: {
      path: ROUTES.ADMIN_PROBLEMS_DETAIL,
      component: ProblemDetailPage,
    },
  },
  dashboardCards: [
    {
      title: "Add Problem",
      description: "Admin-only: Add a new problem",
      icon: (
        <EditNoteIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.ADMIN_PROBLEMS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Problems",
      description: "Admin-only: View, update, or delete problems",
      icon: (
        <SettingsIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />
      ),
      route: ROUTES.ADMIN_PROBLEMS,
      isAdminOnly: true,
    },
    {
      title: "Practice DSA",
      description: "Test your DSA skills by practicing problems",
      icon: (
        <ExtensionIcon
          sx={{ width: 40, height: 40, mb: 2, color: "#274471" }}
        />
      ),
      route: ROUTES.ALGOS_AND_DS,
      isAdminOnly: false,
    },
  ],
  entityStructure: {},
};

export const ProgrammingContentEntity = {
  sectionTitle: "Programming Content",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_PROGRAMMING_CONTENT,
      component: AdminProgrammingContentList,
    },
    add: {
      path: ROUTES.ADMIN_PROGRAMMING_CONTENT_ADD,
      component: AdminProgrammingContentForm,
    },
    update: {
      path: ROUTES.ADMIN_PROGRAMMING_CONTENT_UPDATE,
      component: AdminProgrammingContentForm,
    },
    detail: {
      path: ROUTES.ADMIN_PROGRAMMING_CONTENT_DETAIL,
      component: ProgrammingContentPage,
    },
  },
  dashboardCards: [
    {
      title: "Add Programming Content",
      description: "Admin-only: Add new programming content",
      icon: (
        <EditNoteIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.ADMIN_PROGRAMMING_CONTENT_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Programming Content",
      description: "Admin-only: View, update, or delete programming content",
      icon: (
        <SettingsIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />
      ),
      route: ROUTES.ADMIN_PROGRAMMING_CONTENT,
      isAdminOnly: true,
    },
  ],
  entityStructure: {},
};

export const PricingPlanEntity = {
  sectionTitle: "Premium Plan Management",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_PLANS,
      component: PricingPlan,
    },
    update: {
      path: ROUTES.ADMIN_PLANS_UPDATE,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_PLANS_DETAIL,
      component: PricingDetails,
    },
    add: {
      path: ROUTES.ADMIN_PLANS_ADD,
      component: null,
    },
  },
  backendApiPaths: {
    create: PREMIUM_PLANS,
    getById: PREMIUM_PLANS,
    update: PREMIUM_PLANS,
    delete: `${PREMIUM_PLANS}/:id`,
  },
  dashboardCards: [
    {
      title: "Add Plan",
      description: "Admin-only: Add new plan",
      icon: (
        <AddCardIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.ADMIN_PLANS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Premium Plans",
      description: "Admin-only: View, update, or delete premium plans",
      icon: (
        <CreditCardIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />
      ),
      route: ROUTES.ADMIN_PLANS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        isHiddenInForm: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      name: {
        type: FIELD_TYPES.STRING,
        label: "Plan Name",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      details: {
        type: FIELD_TYPES.STRING,
        label: "Details",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      programmingLanguage: {
        type: FIELD_TYPES.ENUM,
        label: "Programming Language",
        required: true,
        options: Object.keys(PROGRAMMING_LANGUAGES).filter(
          (key) => key !== "colorMap"
        ),
        colorMap: PROGRAMMING_LANGUAGES.colorMap,
        labelMap: PROGRAMMING_LANGUAGES,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      duration: {
        type: FIELD_TYPES.STRING,
        label: "Duration",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      courseFor: {
        type: FIELD_TYPES.ENUM,
        label: "Course For",
        required: true,
        options: Object.keys(COURSE_FOR).filter((key) => key !== "colorMap"),
        colorMap: COURSE_FOR.colorMap,
        labelMap: COURSE_FOR,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      rating: {
        type: FIELD_TYPES.STRING,
        label: "Rating",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      numberOfLectures: {
        type: FIELD_TYPES.STRING,
        label: "Number of Lectures",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      price: {
        type: FIELD_TYPES.NUMBER,
        label: "Price",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
        placeholder: "Enter price in paise (e.g., for ₹3000, enter 300000)",
        validation: {
          min: 0,
          message: "Price must be a non-negative number",
        },
      },
      thumbnail: {
        type: FIELD_TYPES.IMAGE,
        label: "Thumbnail",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        validation: {
          optional: true,
        },
      },
      thumbnailContentType: {
        type: FIELD_TYPES.ENUM,
        label: "Thumbnail Content Type",
        required: true,
        options: Object.keys(THUMBNAIL_CONTENT_TYPE).filter(
          (key) => key !== "colorMap"
        ),
        colorMap: THUMBNAIL_CONTENT_TYPE.colorMap,
        labelMap: THUMBNAIL_CONTENT_TYPE,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      validity: {
        type: FIELD_TYPES.NUMBER,
        label: "Validity (Days)",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      validityUnit: {
        type: FIELD_TYPES.ENUM,
        label: "Validity Unit",
        required: true,
        options: Object.keys(DURATION).filter((key) => key !== "colorMap"),
        colorMap: DURATION.colorMap,
        labelMap: DURATION,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 3,
      },
      peopleEnrolled: {
        type: FIELD_TYPES.NUMBER,
        label: "People Enrolled",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
      },
      description: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Description",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 12,
      },
      contentDetails: {
        type: FIELD_TYPES.NESTED_ARRAY,
        label: "Content Details",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 12,
        editGridXs: 12,
        editGridSm: 12,
        nestedFields: {
          topic: {
            type: FIELD_TYPES.STRING,
            label: "Topic",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          active: {
            type: FIELD_TYPES.BOOLEAN,
            label: "Active",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 2,
          },
          contentDetails: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Sub-Content Details",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              videoTitle: {
                type: FIELD_TYPES.STRING,
                label: "Video Title",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
              videoLink: {
                type: FIELD_TYPES.STRING,
                label: "Video Link",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 8,
                editGridXs: 12,
                editGridSm: 8,
              },
              provider: {
                type: FIELD_TYPES.ENUM,
                label: "Provider",
                options: Object.keys(CONTENT_TYPE).filter(
                  (key) => key !== "colorMap"
                ),
                colorMap: CONTENT_TYPE.colorMap,
                labelMap: CONTENT_TYPE,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
              visibility: {
                type: FIELD_TYPES.ENUM,
                label: "Visibility",
                options: Object.keys(VISIBILITY).filter(
                  (key) => key !== "colorMap"
                ),
                colorMap: VISIBILITY.colorMap,
                labelMap: VISIBILITY,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
              programmingLanguage: {
                type: FIELD_TYPES.ENUM,
                label: "Programming Language",
                options: Object.keys(PROGRAMMING_LANGUAGES).filter(
                  (key) => key !== "colorMap"
                ),
                colorMap: PROGRAMMING_LANGUAGES.colorMap,
                labelMap: PROGRAMMING_LANGUAGES,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
              videoDescription: {
                type: FIELD_TYPES.TEXTAREA,
                label: "Video Description",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 12,
                editGridXs: 12,
                editGridSm: 12,
              },
              active: {
                type: FIELD_TYPES.BOOLEAN,
                label: "Active",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
              previewAvailable: {
                type: FIELD_TYPES.BOOLEAN,
                label: "Preview Available",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 4,
              },
            },
          },
        },
      },
      instructorDetails: {
        type: FIELD_TYPES.NESTED_ARRAY,
        label: "Instructor Details",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 12,
        editGridXs: 12,
        editGridSm: 12,
        nestedFields: {
          name: {
            type: FIELD_TYPES.STRING,
            label: "Instructor Name",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
            validation: {
              regex: /^[a-zA-Z\s]{3,100}$/,
              message: "Instructor Name must be 3-100 letters or spaces",
            },
          },
          profilePic: {
            type: FIELD_TYPES.IMAGE,
            label: "Profile Picture",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
            validation: {
              optional: true,
            },
          },
          instructorDetails: {
            type: FIELD_TYPES.TEXTAREA,
            label: "Instructor Details",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          socialLinks: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Social Links",
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              platform: {
                type: FIELD_TYPES.ENUM,
                label: "Platform",
                options: Object.keys(SOCIAL_PLATFORMS).filter(
                  (key) => key !== "colorMap"
                ),
                labelMap: SOCIAL_PLATFORMS,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              link: {
                type: FIELD_TYPES.STRING,
                label: "Link",
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
            },
          },
        },
      },
    },
  },
  supportsFilter: true,
};

export const PremiumPlanEntity = {
  sectionTitle: "Purchase Management",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_PAYMENTS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_PAYMENTS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_PAYMENTS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_PAYMENTS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    create: PREMIUM_PLAN_PURCHASE_DETAILS,
    getById: PREMIUM_PLAN_PURCHASE_DETAILS,
    getList: PREMIUM_PLAN_PURCHASE_DETAILS,
    update: PREMIUM_PLAN_PURCHASE_DETAILS,
    delete: `${PREMIUM_PLAN_PURCHASE_DETAILS}/:id`,
  },
  dashboardCards: [
    {
      title: "Add Premium Plan",
      description: "Admin-only: Add new premium plan",
      icon: (
        <EditNoteIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.ADMIN_PAYMENTS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Premium Plans Purchase details",
      description:
        "Admin-only: View, update, or delete premium plans purchase details",
      icon: <StoreIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />,
      route: ROUTES.ADMIN_PAYMENTS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      paymentId: {
        type: FIELD_TYPES.STRING,
        label: "Payment ID",
        listTableSticky: true,
        listTableStickyLeft: 80,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      orderId: {
        type: FIELD_TYPES.STRING,
        label: "Order ID",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      paymentStatus: {
        type: FIELD_TYPES.ENUM,
        label: "Payment Status",
        required: true,
        options: Object.keys(PAYMENT_STATUS).filter(
          (key) => key !== "colorMap"
        ),
        isIncludedInPayload: true,
        labelMap: {
          PAYMENT_INITIATED: "Payment Initiated",
          PAYMENT_COMPLETED: "Payment Completed",
          REFUND_UNDER_PROCESS: "Refund Under Process",
          REFUND_COMPLETED: "Refund Completed",
          PAYMENT_FAILED: "Payment Failed",
        },
        colorMap: PAYMENT_STATUS.colorMap,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      signature: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Signature",
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      additionalInformation: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Additional Information",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      userId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: ADMIN_USERS,
          valueField: "id",
          labelField: "login",
          searchField: "login.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
      userLogin: {
        type: FIELD_TYPES.STRING,
        label: "User Login",
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      premiumPlansId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "Premium Plan ID",
        isIncludedInPayload: true,
        required: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: PREMIUM_PLANS,
          valueField: "id",
          labelField: "name",
          searchField: "name.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
      premiumPlansName: {
        type: FIELD_TYPES.STRING,
        label: "Premium Plans Name",
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const ReportEntity = {
  sectionTitle: "Report Management",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_REPORTS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_REPORTS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_REPORTS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_REPORTS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    getList: ADMIN_REPORTS,
    create: ADMIN_REPORTS,
    getById: ADMIN_REPORTS,
    update: ADMIN_REPORTS,
    delete: `${ADMIN_REPORTS}/:id`,
  },
  dashboardCards: [
    {
      title: "Add Report",
      description: "Admin-only: Add new report",
      icon: (
        <ReportProblemIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="primary"
        />
      ),
      route: ROUTES.ADMIN_REPORTS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Reports",
      description: "Admin-only: View, update, or delete reports",
      icon: (
        <BugReportIcon sx={{ width: 40, height: 40, mb: 2 }} color="error" />
      ),
      route: ROUTES.ADMIN_REPORTS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      requestType: {
        type: FIELD_TYPES.ENUM,
        label: "Request Type",
        required: true,
        options: Object.keys(REQUEST_TYPES).filter((key) => key !== "colorMap"),
        labelMap: REQUEST_TYPES,
        colorMap: REQUEST_TYPES.colorMap,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      contactNumber: {
        type: FIELD_TYPES.STRING,
        label: "Contact Number",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      status: {
        type: FIELD_TYPES.ENUM,
        label: "Status",
        required: true,
        options: Object.keys(REQUEST_STATUS).filter(
          (key) => key !== "colorMap"
        ),
        labelMap: REQUEST_STATUS,
        colorMap: REQUEST_STATUS.colorMap,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      responseMessage: {
        type: FIELD_TYPES.STRING,
        label: "Response Message",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      userId: {
        type: FIELD_TYPES.NUMBER,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      userLogin: {
        type: FIELD_TYPES.STRING,
        label: "User Login",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 4,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      issue: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Issue",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 8,
        editGridXs: 12,
        editGridSm: 8,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      imageContentType: {
        type: FIELD_TYPES.ENUM,
        label: "Image Content Type",
        required: true,
        options: Object.keys(THUMBNAIL_CONTENT_TYPE).filter(
          (key) => key !== "colorMap"
        ),
        labelMap: THUMBNAIL_CONTENT_TYPE,
        colorMap: THUMBNAIL_CONTENT_TYPE.colorMap,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      image: {
        type: FIELD_TYPES.IMAGE,
        label: "Image",
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const UserProblemSubmissionEntity = {
  sectionTitle: "User Problem Submissions",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_USER_PROBLEM_SUBMISSIONS,
      component: null,
    },
  },
  backendApiPaths: {
    getList: GET_USER_PROBLEM_SUBMISSION_DETAILS,
    getById: GET_USER_PROBLEM_SUBMISSION_DETAILS,
  },
  dashboardCards: [
    {
      title: "Manage User Problem Submissions",
      description:
        "Admin-only: View, update, or delete user problem submissions",
      icon: (
        <SettingsIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />
      ),
      route: ROUTES.ADMIN_USER_PROBLEM_SUBMISSIONS,
      isAdminOnly: true,
    },
  ],
  isUpdate: false, // Disable Update button
  isAdd: false, // Disable Add button
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.STRING,
        label: "ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      consoleOutput: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Console Output",
        required: true,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      timeTaken: {
        type: FIELD_TYPES.STRING,
        label: "Time Taken",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      memoryUsed: {
        type: FIELD_TYPES.STRING,
        label: "Memory Used",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      consoleError: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Console Error",
        required: true,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      uniqueToken: {
        type: FIELD_TYPES.STRING,
        label: "Unique Token",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      compilationOutput: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Compilation Output",
        required: true,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      executionOutput: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Execution Output",
        required: true,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      status: {
        type: FIELD_TYPES.ENUM,
        label: "Status",
        required: true,
        options: Object.values(SUBMISSION_STATUSES),
        labelMap: {
          RUNTIME_ERROR: "Runtime Error",
          COMPILATION_ERROR: "Compilation Error",
          SUCCESS: "Success",
          TIME_LIMIT_EXCEEDED: "Time Limit Exceeded",
          ACCEPTED: "Accepted",
          INVALID_ANSWER: "Invalid Answer",
        },
        colorMap: SUBMISSION_STATUSES.colorMap,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      totalNumberOfTests: {
        type: FIELD_TYPES.NUMBER,
        label: "Total Number of Tests",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      numberOfSuccessfulTests: {
        type: FIELD_TYPES.NUMBER,
        label: "Number of Successful Tests",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      numberOfFailedTests: {
        type: FIELD_TYPES.NUMBER,
        label: "Number of Failed Tests",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      submittedOn: {
        type: FIELD_TYPES.STRING,
        label: "Submitted On",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      programmingLanguage: {
        type: FIELD_TYPES.STRING,
        label: "Programming Language",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      problemsId: {
        type: FIELD_TYPES.STRING,
        label: "Problems ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      userId: {
        type: FIELD_TYPES.STRING,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      codeSnippet: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Code Snippet",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        isHiddenInTable: true,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      evaluatedTests: {
        type: FIELD_TYPES.NESTED_ARRAY,
        label: "Evaluated Tests",
        required: false,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 12,
        editGridXs: 12,
        editGridSm: 12,
        nestedFields: {
          testCase: {
            type: FIELD_TYPES.STRING,
            label: "Test Case",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          problemInputs: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Problem Inputs",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              variableName: {
                type: FIELD_TYPES.STRING,
                label: "Variable Name",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              value: {
                type: FIELD_TYPES.STRING,
                label: "Value",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              variableType: {
                type: FIELD_TYPES.STRING,
                label: "Variable Type",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
            },
          },
          problemOutputs: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Problem Outputs",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              variableName: {
                type: FIELD_TYPES.STRING,
                label: "Variable Name",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              value: {
                type: FIELD_TYPES.STRING,
                label: "Value",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              variableType: {
                type: FIELD_TYPES.STRING,
                label: "Variable Type",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
            },
          },
          executionOutput: {
            type: FIELD_TYPES.STRING,
            label: "Execution Output",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          stdOut: {
            type: FIELD_TYPES.STRING,
            label: "Standard Output",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          evaluated: {
            type: FIELD_TYPES.BOOLEAN,
            label: "Evaluated",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
        },
      },
      failedTests: {
        type: FIELD_TYPES.NESTED_ARRAY,
        label: "Failed Tests",
        required: false,
        isIncludedInPayload: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 12,
        editGridXs: 12,
        editGridSm: 12,
        nestedFields: {
          testCase: {
            type: FIELD_TYPES.STRING,
            label: "Test Case",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          problemInputs: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Problem Inputs",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              variableName: {
                type: FIELD_TYPES.STRING,
                label: "Variable Name",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              value: {
                type: FIELD_TYPES.STRING,
                label: "Value",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              variableType: {
                type: FIELD_TYPES.STRING,
                label: "Variable Type",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
            },
          },
          problemOutputs: {
            type: FIELD_TYPES.NESTED_ARRAY,
            label: "Problem Outputs",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 12,
            editGridXs: 12,
            editGridSm: 12,
            nestedFields: {
              variableName: {
                type: FIELD_TYPES.STRING,
                label: "Variable Name",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              value: {
                type: FIELD_TYPES.STRING,
                label: "Value",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
              variableType: {
                type: FIELD_TYPES.STRING,
                label: "Variable Type",
                required: false,
                isIncludedInPayload: true,
                viewGridXs: 12,
                viewGridSm: 4,
                editGridXs: 12,
                editGridSm: 6,
              },
            },
          },
          executionOutput: {
            type: FIELD_TYPES.STRING,
            label: "Execution Output",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          stdOut: {
            type: FIELD_TYPES.STRING,
            label: "Standard Output",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
          evaluated: {
            type: FIELD_TYPES.BOOLEAN,
            label: "Evaluated",
            required: false,
            isIncludedInPayload: true,
            viewGridXs: 12,
            viewGridSm: 4,
            editGridXs: 12,
            editGridSm: 6,
          },
        },
      },
    },
  },
  supportsFilter: true,
};

export const UserPlanEntity = {
  sectionTitle: "User Plans",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_USER_PLANS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_USER_PLANS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_USER_PLANS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_USER_PLANS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    create: ADMIN_USER_PLANS,
    getById: ADMIN_USER_PLANS,
    getList: ADMIN_USER_PLANS,
    update: ADMIN_USER_PLANS,
  },
  dashboardCards: [
    {
      title: "Add User Plan",
      description: "Admin-only: Add new user plan",
      icon: (
        <EditNoteIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.ADMIN_USER_PLANS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage User Plans",
      description: "Admin-only: View, update, or delete user plans",
      icon: (
        <SettingsIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />
      ),
      route: ROUTES.ADMIN_USER_PLANS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        required: true,
        isIncludedInPayload: true,
        isHiddenInForm: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      status: {
        type: FIELD_TYPES.ENUM,
        label: "Status",
        required: true,
        options: Object.keys(STATUS).filter((key) => key !== "colorMap"),
        labelMap: {
          ACTIVE: "Active",
          EXPIRED: "Expired",
          RETURNED: "Returned",
        },
        colorMap: STATUS.colorMap,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 4,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      fromDate: {
        type: FIELD_TYPES.STRING,
        label: "From Date",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 4,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      toDate: {
        type: FIELD_TYPES.STRING,
        label: "To Date",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 4,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      userId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: ADMIN_USERS,
          valueField: "id",
          labelField: "login",
          searchField: "login.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
      userLogin: {
        type: FIELD_TYPES.STRING,
        label: "User Login",
        required: false,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      premiumPlansId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "Premium Plan ID",
        isIncludedInPayload: true,
        required: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: PREMIUM_PLANS,
          valueField: "id",
          labelField: "name",
          searchField: "name.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
      premiumPlansName: {
        type: FIELD_TYPES.STRING,
        label: "Premium Plans Name",
        required: false,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      paymentDetails: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Payment Details",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 6,
        editGridXs: 12,
        editGridSm: 6,
        isHiddenInTable: true,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      additionalDetails: {
        type: FIELD_TYPES.TEXTAREA,
        label: "Additional Details",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 6,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const Tags = {
  sectionTitle: "Tags",
  uiPaths: {
    list: {
      path: ROUTES.TAGS,
      component: null,
    },
    update: {
      path: ROUTES.TAGS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.TAGS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.TAGS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    create: TAGS,
    getById: TAGS,
    getList: TAGS,
    update: TAGS,
  },
  dashboardCards: [
    {
      title: "Add Tag",
      description: "Admin-only: Add new tag",
      icon: (
        <EditNoteIcon sx={{ width: 40, height: 40, mb: 2 }} color="primary" />
      ),
      route: ROUTES.TAGS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Tags",
      description: "Admin-only: View, update, or delete tags",
      icon: <TagIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />,
      route: ROUTES.TAGS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        required: true,
        isIncludedInPayload: true,
        isHiddenInForm: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      name: {
        type: FIELD_TYPES.STRING,
        label: "Name",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      description: {
        type: FIELD_TYPES.STRING,
        label: "Description",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      image: {
        type: FIELD_TYPES.IMAGE,
        label: "Image",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        isHiddenInTable: true,
      },
      imageContentType: {
        type: FIELD_TYPES.STRING,
        label: "Image Content Type",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      active: {
        type: FIELD_TYPES.BOOLEAN,
        label: "Active",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const UserStreaks = {
  sectionTitle: "User Streaks",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_USER_STREAKS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_USER_STREAKS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_USER_STREAKS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_USER_STREAKS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    create: ADMIN_USER_STREAKS,
    getById: ADMIN_USER_STREAKS,
    getList: ADMIN_USER_STREAKS,
    update: ADMIN_USER_STREAKS,
  },
  dashboardCards: [
    {
      title: "Add User Streak",
      description: "Admin-only: Add new user streak",
      icon: (
        <CalendarMonthIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="primary"
        />
      ),
      route: ROUTES.ADMIN_USER_STREAKS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage User Streaks",
      description: "Admin-only: View, update, or delete user streaks",
      icon: (
        <CalendarMonthIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="success"
        />
      ),
      route: ROUTES.ADMIN_USER_STREAKS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        required: true,
        isIncludedInPayload: true,
        isHiddenInForm: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      currentStreak: {
        type: FIELD_TYPES.NUMBER,
        label: "Current Streak",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      longestStreak: {
        type: FIELD_TYPES.NUMBER,
        label: "Longest Streak",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      lastSubmissionDate: {
        type: FIELD_TYPES.DATE,
        label: "Last Submission Date",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        isHiddenInTable: true,
      },
      userId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: ADMIN_USERS,
          valueField: "id",
          labelField: "login",
          searchField: "login.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
    },
  },
  supportsFilter: true,
};

export const ProblemLists = {
  sectionTitle: "Problem Lists",
  uiPaths: {
    list: {
      path: ROUTES.ADMIN_PROBLEM_LISTS,
      component: null,
    },
    update: {
      path: ROUTES.ADMIN_PROBLEM_LISTS_UPDATE,
      component: null,
    },
    add: {
      path: ROUTES.ADMIN_PROBLEM_LISTS_ADD,
      component: null,
    },
    detail: {
      path: ROUTES.ADMIN_PROBLEM_LISTS_DETAIL,
      component: null,
    },
  },
  backendApiPaths: {
    create: ADMIN_PROBLEM_LISTS,
    getById: ADMIN_PROBLEM_LISTS,
    getList: ADMIN_PROBLEM_LISTS,
    update: ADMIN_PROBLEM_LISTS,
  },
  dashboardCards: [
    {
      title: "Add Problem List",
      description: "Admin-only: Add new problem list",
      icon: (
        <PlaylistAddIcon
          sx={{ width: 40, height: 40, mb: 2 }}
          color="primary"
        />
      ),
      route: ROUTES.ADMIN_PROBLEM_LISTS_ADD,
      isAdminOnly: true,
    },
    {
      title: "Manage Problem Lists",
      description: "Admin-only: View, update, or delete problem lists",
      icon: <ListIcon sx={{ width: 40, height: 40, mb: 2 }} color="success" />,
      route: ROUTES.ADMIN_PROBLEM_LISTS,
      isAdminOnly: true,
    },
  ],
  entityStructure: {
    fields: {
      id: {
        type: FIELD_TYPES.NUMBER,
        label: "ID",
        listTableSticky: true,
        listTableStickyLeft: 0,
        required: true,
        isIncludedInPayload: true,
        isHiddenInForm: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      listName: {
        type: FIELD_TYPES.TEXT,
        label: "List Name",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      description: {
        type: FIELD_TYPES.TEXT,
        label: "Description",
        required: false,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
      visibility: {
        type: FIELD_TYPES.SELECT,
        label: "Visibility",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        options: Object.keys(VISIBILITY).filter((key) => key !== "colorMap"),
        colorMap: VISIBILITY.colorMap,
        labelMap: VISIBILITY,
        filterOperators: ["equals", "in", "specified"],
      },
      userId: {
        type: FIELD_TYPES.FOREIGN_KEY,
        label: "User ID",
        required: true,
        isIncludedInPayload: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
        foreignKeyConfig: {
          apiPath: ADMIN_USERS,
          valueField: "id",
          labelField: "login",
          searchField: "login.contains",
          params: {
            size: 20,
            page: 0,
            sort: "id,asc",
          },
        },
      },
      userLogin: {
        type: FIELD_TYPES.TEXT,
        label: "User Login",
        required: false,
        isIncludedInPayload: false,
        isHiddenInForm: true,
        isHiddenInTable: true,
        viewGridXs: 12,
        viewGridSm: 4,
        editGridXs: 12,
        editGridSm: 6,
        filterOperators: ["equals", "contains", "in", "specified"],
      },
    },
  },
  supportsFilter: true,
};

export const ALL_ENTITIES = [
  ProblemEntity,
  UserEntity,
  PricingPlanEntity,
  PremiumPlanEntity,
  ReportEntity,
  UserProblemSubmissionEntity,
  UserPlanEntity,
  Tags,
  UserStreaks,
  ProblemLists,
  ProgrammingContentEntity,
];
